import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import PublicLayout from '../../components/layout/PublicLayout';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import SearchableSelect from '../../components/common/SearchableSelect';
import { API_BASE_URL } from '../../config';
import { authAPI, productAPI, enumAPI, platformAPI, mediatorAPI, orderAPI } from '../../services/api';
import { publicOrderAPI } from '../../services/publicApi';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import RefundPolicyNotice from '../../components/common/RefundPolicyNotice';

import FlipkartDemoSS from '../../flipkart-order-ss.png';
import AmazonDemoSS from '../../amazon-order-ss.png';

const formatFeedbackLabel = (value) =>
  value
    ? value
        .toString()
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase())
    : 'Feedback Type';

const OrderForm = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, getOrdersRoute } = useAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Add resize listener for responsiveness
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [screenshot, setScreenshot] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editOrderId, setEditOrderId] = useState(null);
  const [isRefillMode, setIsRefillMode] = useState(false);
  const [isHydratingOrder, setIsHydratingOrder] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [platforms, setPlatforms] = useState([]);
  const [products, setProducts] = useState([]);
  const [mediators, setMediators] = useState([]);
  const [dealOrderTypes, setDealOrderTypes] = useState([]);
  const [loadingEnums, setLoadingEnums] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false); // Prevent duplicate calls
  const [userEmail, setUserEmail] = useState('');
  const linkHydratedRef = useRef(false);
  const linkMediatorIdRef = useRef(null);
  const previousProductCodeRef = useRef(null);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue
  } = useForm({
    defaultValues: {
      orderStatus: 'ORDER_FORM_FILLED',
      userEmail: '',
    },
    mode: 'onChange'
  });

  const selectedProductCode = watch('productCode');
  const selectedPlatformId = watch('platformId');
  const selectedFeedbackType = watch('dealFeedbackType');

  // Platform order ID formats
  const platformOrderIdFormats = {
    'AMAZON': {
      pattern: /^\d{3}-\d{7}-\d{7}$/,
      placeholder: '123-1234567-1234567',
      description: 'Format: 3 digits – 7 digits – 7 digits'
    },
    'FLIPKART': {
      pattern: /^OD\d{16,18}$/,
      placeholder: 'OD123456789012345678',
      description: 'Format: OD + 16-18 digits'
    },
    'MEESHO': {
      pattern: /^\d{10,20}$/,
      placeholder: '1234567890',
      description: 'Format: 10-15 digits'
    },
    'SNAPDEAL': {
      pattern: /^\d{10,20}$/,
      placeholder: '12345678901',
      description: 'Format: 10-12 digits'
    },
    'MYNTRA': {
      pattern: /^\d{15,25}$/,
      placeholder: '122345678901234567',
      description: 'Format: 15-18 digits'
    },
    'AJIO': {
      pattern: /^(FL|AJ)\d{10,25}$/,
      placeholder: 'FL1234567890',
      description: 'Format: FL or AJ + digits'
    },
    'NYKAA': {
      pattern: /^NYK[\d-]+$/,
      placeholder: 'NYK123456789',
      description: 'Format: NYK + digits (may include hyphens)'
    },
    'SHOPIFY': {
      pattern: /^[#\dORD]+[\d]+$/,
      placeholder: '#1234',
      description: 'Format: Various (numeric or alphanumeric)'
    },
    'EBAY': {
      pattern: /^\d{2}-\d{5}-\d{5}$/,
      placeholder: '12-12345-12345',
      description: 'Format: Numeric with hyphens'
    },
    'ALIEXPRESS': {
      pattern: /^\d{14,18}$/,
      placeholder: '8123456789012345',
      description: 'Format: 14-18 digits'
    },
    'BLINKIT': {
      pattern: /^ORD\d{10,20}$/i,
      placeholder: 'ORD12345678901',
      description: 'Format: ORD + 10-20 digits'
    },
    'ZEPTO': {
      pattern: /^[A-Z0-9-]{8,30}$/i,
      placeholder: 'Enter Zepto order ID',
      description: 'Format: Alphanumeric order ID'
    },
    'INSTAMART': {
      pattern: /^[A-Z0-9-]{8,30}$/i,
      placeholder: 'Enter Instamart order ID',
      description: 'Format: Alphanumeric order ID'
    }
  };

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^\d{10}$/; // Exactly 10 digits
    return phoneRegex.test(phone);
  };

  const formatOrderId = (orderId, platformName) => {
    if (!orderId || !platformName) return orderId;
    
    const format = platformOrderIdFormats[platformName.toUpperCase()];
    const cleanId = orderId.trim().replace(/\s+/g, '');
    
    if (platformName.toUpperCase() === 'AMAZON' && cleanId.replace(/-/g, '').length === 17) {
      const digits = cleanId.replace(/-/g, '');
      return `${digits.slice(0, 3)}-${digits.slice(3, 10)}-${digits.slice(10, 17)}`;
    }

    if (platformName.toUpperCase() === 'BLINKIT') {
      return cleanId.toUpperCase();
    }

    if (!format) return cleanId;
    
    return cleanId;
  };

  // Handle order ID formatting based on platform
  const handleOrderIdChange = (e) => {
    const value = e.target.value;
    const platformName = selectedPlatform?.platformName;
    
    if (platformName) {
      const formattedValue = formatOrderId(value, platformName);
      setValue('orderId', formattedValue);
    }
  };

  // Load enums and products on mount - only once
  const loadEnumsAndProducts = useCallback(async () => {
    // Prevent duplicate calls
    if (loadingEnums) {
      console.log('API call already in progress, skipping...');
      return;
    }
    
    setLoadingEnums(true);
    try {
      console.log('Starting to load form data...');
      const apiUrl = API_BASE_URL;
      console.log('API Base URL:', apiUrl);
      
      // Test API connectivity first
      try {
        console.log('Testing API connectivity to:', `${apiUrl}/platforms`);
        
        // First try with raw fetch to see the exact response
        try {
          const rawResponse = await fetch(`${apiUrl}/platforms`);
          console.log('Raw fetch response:', {
            status: rawResponse.status,
            statusText: rawResponse.statusText,
            ok: rawResponse.ok,
            headers: Object.fromEntries(rawResponse.headers.entries())
          });
          
          if (rawResponse.ok) {
            const rawData = await rawResponse.json();
            console.log('Raw fetch data:', rawData);
          } else {
            const rawError = await rawResponse.text();
            console.log('Raw fetch error:', rawError);
          }
        } catch (rawFetchError) {
          console.log('Raw fetch failed:', rawFetchError);
        }
        
        // Now try with axios
        const testResponse = await api.get('/platforms');
        console.log('Axios test API call successful:', testResponse);
        console.log('Response status:', testResponse.status);
        console.log('Response data:', testResponse.data);
      } catch (testError) {
        console.error('API connectivity test failed:', testError);
        console.error('Error details:', {
          message: testError.message,
          code: testError.code,
          status: testError.response?.status,
          statusText: testError.response?.statusText,
          data: testError.response?.data,
          config: testError.config
        });
        
        if (testError.code === 'ERR_NETWORK' || testError.message.includes('Network Error')) {
          toast.error(`Network Error: Cannot connect to ${apiUrl}. Please check:\n1. Backend is running\n2. Correct port (8083)\n3. No firewall blocking\n4. CORS is configured`);
        } else if (testError.response?.status === 404) {
          toast.error('API endpoint not found. Check if backend endpoints match');
        } else if (testError.response?.status === 401) {
          toast.error('Authentication error. Please check your login token');
        } else if (testError.response?.status === 403) {
          toast.error('Access forbidden. Check if you have permission to access this endpoint');
        } else if (testError.response?.status === 500) {
          toast.error('Server error. Check backend logs for details');
        } else if (testError.response?.status) {
          toast.error(`API Error: ${testError.response.status} - ${testError.response.statusText || 'Unknown Error'}`);
        } else {
          toast.error(`API Error: ${testError.message || 'Unknown network error'}`);
        }
        
        setLoadingEnums(false);
        return;
      }
      
      // Load active platforms
      try {
        console.log('Calling platformAPI.getPlatforms()...');
        const platformsResponse = await platformAPI.getPlatforms();
        console.log('Platforms API call successful:', platformsResponse);
        console.log('Platforms response status:', platformsResponse.status);
        console.log('Platforms response data:', platformsResponse.data);
        console.log('Platforms data type:', typeof platformsResponse.data);
        console.log('Platforms data length:', platformsResponse.data?.length);
        
        const platformsData = platformsResponse.data || [];
        console.log('Setting platforms state to:', platformsData);
        setPlatforms(platformsData);
        
      } catch (platformError) {
        console.error('Failed to load platforms:', platformError);
        console.error('Platform error response:', platformError.response);
        // Try with direct API call
        try {
          console.log('Trying fallback with direct API call...');
          const fallbackPlatformsResponse = await api.get('/platforms');
          console.log('Fallback platforms API call successful:', fallbackPlatformsResponse);
          console.log('Fallback platforms data:', fallbackPlatformsResponse.data);
          setPlatforms(fallbackPlatformsResponse.data || []);
        } catch (fallbackError) {
          console.error('Fallback platforms also failed:', fallbackError);
          toast.error('Failed to load platforms');
          setPlatforms([]);
        }
      }
      
      // Load active products (using the correct endpoint)
      try {
        const productsResponse = await productAPI.getActiveProducts();
        console.log('Products API call successful:', productsResponse);
        console.log('Products data:', productsResponse.data);
        const productsData = productsResponse.data || [];
        setProducts(productsData);
      } catch (productError) {
        console.error('Failed to load products:', productError);
        console.error('Product error response:', productError.response);
        // Try with direct API call
        try {
          const fallbackProductsResponse = await api.get('/products/active');
          console.log('Fallback products API call successful:', fallbackProductsResponse);
          setProducts(fallbackProductsResponse.data || []);
        } catch (fallbackError) {
          console.error('Fallback products also failed:', fallbackError);
          toast.error('Failed to load products');
          setProducts([]);
        }
      }
      
      // Load enums using the enum API
      try {
        const [feedbackTypesResponse, orderTypesResponse] = await Promise.all([
          enumAPI.getDealFeedbackType(),
          enumAPI.getDealOrderType()
        ]);
        
        console.log('Feedback types API call successful:', feedbackTypesResponse);
        console.log('Feedback types data:', feedbackTypesResponse.data);
        console.log('Order types API call successful:', orderTypesResponse);
        console.log('Order types data:', orderTypesResponse.data);

        setDealOrderTypes(orderTypesResponse.data || []);
      } catch (enumError) {
        console.error('Failed to load enums:', enumError);
        console.error('Enum error response:', enumError.response);
        toast.error('Failed to load form options');
        setDealOrderTypes([]);
      }
      
    } catch (error) {
      console.error('General error in loadEnumsAndProducts:', error);
      toast.error('Failed to load form data');
      // Set empty arrays if API fails
      setPlatforms([]);
      setProducts([]);
      setMediators([]);
      setDealOrderTypes([]);
    } finally {
      setLoadingEnums(false);
    }
  }, [loadingEnums]);

  useEffect(() => {
    if (!dataLoaded) {
      console.log('Loading form data for the first time...');
      loadEnumsAndProducts();
      setDataLoaded(true);
    }
  }, [dataLoaded, loadEnumsAndProducts]);

  const applyFeedbackConfig = useCallback((config, product) => {
    if (!config) return;
    setValue('dealFeedbackType', config.feedbackType, { shouldValidate: true });
    setValue('payoutType', config.payoutType || 'LESS');
    setValue('lessAmount', config.payoutValue ?? '');
    setValue('payoutValue', config.payoutValue ?? '');
    if (product?.productAmount != null) {
      setValue('orderAmount', product.productAmount);
    }
  }, [setValue]);

  const getOpenFeedbackConfigs = useCallback((product) =>
    (product?.feedbackConfigs || []).filter(
      (config) => Number(config?.remainingSlots || 0) > 0
    ),
  []);

  useEffect(() => {
    if (isEditMode || !dataLoaded || products.length === 0 || linkHydratedRef.current) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const prefillProductId = params.get('productId');
    const prefillMediatorId = params.get('mediatorId');
    const prefillFeedbackType = params.get('feedbackType');

    if (!prefillProductId || !prefillMediatorId) {
      return;
    }

    linkHydratedRef.current = true;
    linkMediatorIdRef.current = prefillMediatorId;

    const hydrateFromLink = async () => {
      const product = products.find((item) => String(item.id) === String(prefillProductId));
      if (!product) {
        toast.error('Product from link is not available');
        return;
      }

      setIsHydratingOrder(true);
      previousProductCodeRef.current = product.id;

      const openConfigs = getOpenFeedbackConfigs(product);
      const feedback =
        (prefillFeedbackType
          ? openConfigs.find((config) => config.feedbackType === prefillFeedbackType)
          : null) ||
        (openConfigs.length === 1 ? openConfigs[0] : null);

      setValue('productCode', product.id);

      if (product.platformId) {
        setValue('platformId', product.platformId, { shouldValidate: true });
      }
      if (product.dealOrderType) {
        setValue('dealOrderType', product.dealOrderType, { shouldValidate: true });
      }

      if (feedback) {
        applyFeedbackConfig(feedback, product);
      }

      setIsHydratingOrder(false);
    };

    hydrateFromLink();
  }, [applyFeedbackConfig, dataLoaded, getOpenFeedbackConfigs, isEditMode, products, setValue]);

  // Load user profile and set email if authenticated
  useEffect(() => {
    const loadUserProfile = async () => {
      if (isAuthenticated) {
        try {
          // Always fetch fresh user profile to ensure we have the correct email
          const response = await authAPI.getProfile();
          
          let email = null;
          
          // Try different possible locations for the email
          if (response.data?.data?.email) {
            email = response.data.data.email;
          } else if (response.data?.email) {
            email = response.data.email;
          } else if (user?.email) {
            email = user.email;
          }
          
          if (email) {
            setUserEmail(email);
            setValue('userEmail', email, { shouldValidate: true });
          } else {
            // Try to get from localStorage as last resort
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
              try {
                const parsedUser = JSON.parse(storedUser);
                if (parsedUser.email) {
                  email = parsedUser.email;
                  setUserEmail(email);
                  setValue('userEmail', email, { shouldValidate: true });
                }
              } catch (e) {
                // Silently handle parsing error
              }
            }
          }
        } catch (error) {
          // Fallback to localStorage on error
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            try {
              const parsedUser = JSON.parse(storedUser);
              if (parsedUser.email) {
                setUserEmail(parsedUser.email);
                setValue('userEmail', parsedUser.email, { shouldValidate: true });
              }
            } catch (e) {
              // Silently handle parsing error
            }
          }
        }
      }
    };

    loadUserProfile();
  }, [isAuthenticated, user, setValue]);

  // Update hidden field when userEmail changes
  useEffect(() => {
    if (userEmail) {
      setValue('userEmail', userEmail, { shouldValidate: true });
    }
  }, [userEmail, setValue]);

  // Filter products when platform changes (now independent - just update selected platform)
  useEffect(() => {
    if (selectedPlatformId && platforms) {
      const platform = platforms.find(p => String(p.id) === String(selectedPlatformId));
      setSelectedPlatform(platform);
    } else {
      setSelectedPlatform(null);
    }
  }, [selectedPlatformId, platforms]);

  // Update selected product and auto-set platform + deal order type
  useEffect(() => {
    const productChanged = previousProductCodeRef.current !== selectedProductCode;
    previousProductCodeRef.current = selectedProductCode;

    if (selectedProductCode && products) {
      const product = products.find(p => String(p.id) === String(selectedProductCode));
      setSelectedProduct(product);
      if (product) {
        if (product.platformId) {
          setValue('platformId', product.platformId, { shouldValidate: true });
          setSelectedPlatform({
            id: product.platformId,
            platformName: product.platformName
          });
        }
        if (product.dealOrderType) {
          setValue('dealOrderType', product.dealOrderType, { shouldValidate: true });
        }
      }
    } else {
      setSelectedProduct(null);
      setSelectedPlatform(null);
    }

    if (isHydratingOrder || !productChanged) {
      return;
    }

    setValue('dealFeedbackType', '');
    setValue('mediatorId', '');
    setValue('payoutValue', '');
    setValue('lessAmount', '');
    setMediators([]);
  }, [isHydratingOrder, selectedProductCode, products, setValue]);

  useEffect(() => {
    if (isHydratingOrder || isEditMode || !selectedProduct || selectedFeedbackType) {
      return;
    }

    const openConfigs = getOpenFeedbackConfigs(selectedProduct);
    if (openConfigs.length === 1) {
      applyFeedbackConfig(openConfigs[0], selectedProduct);
    }
  }, [
    applyFeedbackConfig,
    getOpenFeedbackConfigs,
    isEditMode,
    isHydratingOrder,
    selectedFeedbackType,
    selectedProduct
  ]);

  const availableFeedbackConfigs = (selectedProduct?.feedbackConfigs || []).filter(
    (config) => Number(config?.remainingSlots || 0) > 0
  );

  const selectedFeedbackConfig =
    availableFeedbackConfigs.find((config) => config.feedbackType === selectedFeedbackType) || null;

  useEffect(() => {
    if (isHydratingOrder) {
      return;
    }
    if (selectedFeedbackConfig) {
      setValue('payoutType', selectedFeedbackConfig.payoutType || 'LESS');
      setValue('lessAmount', selectedFeedbackConfig.payoutValue ?? '');
      setValue('payoutValue', selectedFeedbackConfig.payoutValue ?? '');
      if (selectedProduct?.productAmount != null) {
        setValue('orderAmount', selectedProduct.productAmount);
      }
    } else if (!linkMediatorIdRef.current) {
      setValue('payoutValue', '');
      setValue('lessAmount', '');
    }
  }, [isHydratingOrder, selectedFeedbackConfig, selectedProduct, setValue]);

  useEffect(() => {
    const fetchMediators = async () => {
      if (!selectedProduct?.id) {
        setMediators([]);
        return;
      }

      try {
        const response = await mediatorAPI.getMediatorsByProduct(selectedProduct.id);
        const rawMediatorData = response.data?.data || response.data || [];
        const mediatorData = Array.isArray(rawMediatorData) ? rawMediatorData : [];
        setMediators(mediatorData);

        const linkedMediatorId = linkMediatorIdRef.current;
        if (linkedMediatorId) {
          const matchedMediator = mediatorData.find(
            (mediator) => String(mediator.id) === String(linkedMediatorId)
          );
          if (matchedMediator) {
            setValue('mediatorId', linkedMediatorId, { shouldValidate: true });
            linkMediatorIdRef.current = null;
          } else {
            toast.error('Mediator from link is not available for this product');
            linkMediatorIdRef.current = null;
          }
        }
      } catch (error) {
        console.error('Failed to fetch mediators:', error);
        setMediators([]);
      }
    };

    fetchMediators();
  }, [selectedProduct, setValue]);

  const hasOpenSlots = (product) =>
    (product?.feedbackConfigs || []).some((config) => Number(config?.remainingSlots || 0) > 0);

  // Filter active products with available slots (skip filter in edit/refill mode)
  const activeProducts = (products?.filter(p => p.active) || [])
    .filter((p) => isEditMode || isRefillMode || hasOpenSlots(p));
  const platformOptions = (platforms || []).map((platform) => ({
    value: platform.id,
    label: platform.platformName || platform.name || `Platform ${platform.id}`
  }));
  const productOptions = activeProducts.map((product) => ({
    value: product.id,
    label: `${product.productCode} - ${product.productName || 'Product'}`
  }));
  const feedbackTypeOptions = availableFeedbackConfigs.map((config) => ({
    value: config.feedbackType,
    label: `${formatFeedbackLabel(config.feedbackType)} (${config.remainingSlots}/${config.totalSlots} slots left)`
  }));
  const mediatorOptions = mediators.map((mediator) => ({
    value: mediator.id,
    label: mediator.mediatorCode || mediator.mediatorName || mediator.username || `Mediator ${mediator.id}`
  }));
  const dealOrderTypeOptions = dealOrderTypes.map((type) => {
    const value = typeof type === 'string' ? type : type.code;
    const label = typeof type === 'string' ? formatFeedbackLabel(type) : type.label || formatFeedbackLabel(type.code);
    return { value, label };
  });

  const handleScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      console.log('Screenshot file selected:', file);
      console.log('File name:', file.name);
      console.log('File type:', file.type);
      console.log('File size:', file.size);
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      
      setScreenshot(file);
      console.log('Screenshot state updated:', file);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const editId = params.get('edit');
    const refill = params.get('refill') === 'true';
    if (editId) {
      setIsEditMode(true);
      setIsRefillMode(refill);
      setEditOrderId(editId);
      loadOrderForEdit(editId);
    }
  }, []);

  const loadOrderForEdit = async (id) => {
    try {
      setIsHydratingOrder(true);
      const response = await orderAPI.getOrder(id);
      const order = response.data.data;
      if (order) {
        setValue('platformId', order.platformId);
        setValue('productCode', order.productId);
        setValue('orderId', order.orderId);
        setValue('whatsappNumber', order.whatsappNumber);
        setValue('reviewerName', order.reviewerName);
        setValue('orderDate', order.orderDate);
        setValue('mediatorId', order.mediatorId);
        setValue('orderAmount', order.orderAmount);
        setValue('dealFeedbackType', order.dealFeedbackType);
        setValue('dealOrderType', order.dealOrderType);
        setValue('lessAmount', order.lessAmount);
        setValue('payoutType', order.payoutType || 'LESS');
        setValue('payoutValue', order.payoutValue || order.lessAmount || '');
        if (order.userEmail) {
          setUserEmail(order.userEmail);
          setValue('userEmail', order.userEmail);
        }
        
        // Auto-fill screenshot if it exists
        if (order.orderScreenshot) {
          setScreenshot(order.orderScreenshot);
        }
        
        // Auto-confirm for refill mode
        setConfirmed(true);
      }
    } catch (error) {
      console.error('Failed to load order for edit:', error);
      toast.error('Failed to load order details');
    } finally {
      setIsHydratingOrder(false);
    }
  };

  const onSubmit = async (data) => {
    if (!selectedFeedbackConfig) {
      toast.error('Please select a valid feedback type for this product');
      return;
    }

    if (!confirmed || !screenshot) {
      toast.error('Please upload screenshot and confirm the details');
      return;
    }

    setSubmitting(true);

    try {

      const formData = new FormData();

      const resolvedPayoutType =
        data.payoutType || selectedFeedbackConfig?.payoutType || 'LESS';
      const resolvedPayoutValue =
        parseFloat(data.lessAmount || selectedFeedbackConfig?.payoutValue || 0);

      const orderFormDto = {
        id: isEditMode ? editOrderId : null,
        orderId: data.orderId,
        oldOrderId: null,
        productId: data.productCode,
        userEmail: data.userEmail,
        whatsappNumber: data.whatsappNumber,
        reviewerName: data.reviewerName,
        orderDate: data.orderDate,
        mediatorId: data.mediatorId,
        orderAmount: parseFloat(data.orderAmount),
        dealFeedbackType: data.dealFeedbackType,
        dealOrderType: data.dealOrderType,
        payoutType: resolvedPayoutType,
        payoutValue: resolvedPayoutValue,
        lessAmount: resolvedPayoutValue,
        orderStatus: 'ORDER_FORM_FILLED',
      };

      formData.append(
          'data',
          new Blob(
              [JSON.stringify(orderFormDto)],
              { type: 'application/json' }
          )
      );

      formData.append('orderScreenshot', screenshot);

      // API Call - Use updateOrderForm if in refill mode, otherwise createOrder
      let response;
      if (isRefillMode) {
        response = await orderAPI.updateOrderForm(editOrderId, formData);
      } else {
        response = await publicOrderAPI.createOrder(formData);
      }

      console.log('Order submission response:', response);

      toast.success('Order submitted successfully! A confirmation email has been sent to your email address.');
      navigate('/track');

    } catch (error) {
      console.error('=== Order submission failed ===');
      console.error(error);

      if (error.response?.data?.message?.includes('Content-Type')) {
        toast.error('Server error: Invalid content format. Please try again.');
      } else if (error.code === 'ERR_NETWORK') {
        toast.error('Network error: Unable to connect to server.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to submit order');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout title="Submit Order" showBack onBackClick={() => {
      if (isAuthenticated) {
        navigate(getOrdersRoute());
      } else {
        navigate('/');
      }
    }}>
      <div style={{ maxWidth: '896px', margin: '0 auto', padding: isMobile ? '0 12px' : '0' }}>
        {/* Loading State */}
        {loadingEnums && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '60px 20px',
            textAlign: 'center'
          }}>
            <div>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
              <h3 style={{ 
                fontFamily: 'Outfit, sans-serif',
                fontWeight: '600', 
                fontSize: '18px', 
                color: 'var(--text)',
                marginBottom: '8px'
              }}>
                Loading Form Data...
              </h3>
              <p style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
                Please wait while we load the required information
              </p>
            </div>
          </div>
        )}
        
        {!loadingEnums && (
          <>
            {/* Banner */}
            {showBanner && (
              <div style={{ marginBottom: '16px' }}>
                <RefundPolicyNotice compact />
              </div>
            )}

            {showBanner && (
              <div 
                style={{
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  background: 'var(--accent-light)',
                  border: `1px solid var(--accent)33`,
                }}
              >
                <Icon name="info" size={16} color="var(--accent)" />
                <span style={{ 
                  flex: '1', 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: 'var(--accent)' 
                }}>
                  Thanks for shopping with us! Fill in the correct details in the form.
                </span>
                <button
                  onClick={() => setShowBanner(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--accent)',
                    cursor: 'pointer'
                  }}
                >
                  <Icon name="x" size={13} />
                </button>
              </div>
            )}

        {/* Main Form */}
        <div className="card" style={{ overflow: 'hidden' }}>
          {/* Header */}
          <div 
            style={{
              padding: isMobile ? '20px 16px' : '24px',
              textAlign: 'center',
              background: 'linear-gradient(135deg, #312E81, var(--accent))',
            }}
          >
            <h2 
              className="outfit"
              style={{ 
                fontWeight: '700', 
                fontSize: isMobile ? '20px' : '24px', 
                marginBottom: '8px',
                color: 'white' 
              }}
            >
              Submit Your Order
            </h2>
            <p 
              style={{ 
                fontSize: isMobile ? '13px' : '14px',
                color: 'rgba(255,255,255,0.72)' 
              }}
            >
              Please fill in the details accurately to ensure quick processing.
            </p>
          </div>

          {/* Form Content */}
          <div style={{ padding: isMobile ? '16px' : '24px' }}>
            <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Product Selection */}
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Product *</label>
                <input type="hidden" {...register('platformId')} />
                <input type="hidden" {...register('dealOrderType')} />
                <input type="hidden" {...register('productCode', { required: 'Please select a product' })} />
                <SearchableSelect
                  options={productOptions}
                  value={selectedProductCode}
                  onChange={(value) => setValue('productCode', value, { shouldValidate: true, shouldDirty: true })}
                  placeholder={loadingEnums ? 'Loading products...' : 'Select a product'}
                  searchPlaceholder="Type product code or name..."
                  disabled={loadingEnums}
                  error={Boolean(errors.productCode)}
                  noOptionsText="No products available"
                  disabledText="Products are loading"
                />
                {errors.productCode && (
                  <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.productCode.message}</p>
                )}
                {selectedProduct && (
                  <div style={{ marginTop: '10px', padding: '12px', borderRadius: '10px', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text)' }}>{selectedProduct.productName}</div>
                    <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--text-muted)' }}>
                      Platform: {selectedProduct.platformName || selectedPlatform?.platformName || '-'}
                    </div>
                    <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--text-muted)' }}>
                      Deal Order Type: {formatFeedbackLabel(selectedProduct.dealOrderType || watch('dealOrderType'))}
                    </div>
                    <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--text-muted)' }}>
                      Product Amount: ₹{selectedProduct.productAmount ?? '-'}
                    </div>
                    <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--text-muted)' }}>
                      {selectedProduct.restrictedToAssignedMediators === false
                        ? 'This product is open to all active mediators.'
                        : 'Only assigned mediators with remaining slots can submit this deal.'}
                    </div>
                  </div>
                )}
              </div>

              {selectedProduct && (
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Feedback Type *</label>
                  <input type="hidden" {...register('dealFeedbackType', { required: 'Please select feedback type' })} />
                  <SearchableSelect
                    options={feedbackTypeOptions}
                    value={selectedFeedbackType}
                    onChange={(value) => setValue('dealFeedbackType', value, { shouldValidate: true, shouldDirty: true })}
                    placeholder={availableFeedbackConfigs.length ? 'Select type' : 'No feedback types available'}
                    searchPlaceholder="Type feedback type..."
                    error={Boolean(errors.dealFeedbackType)}
                    noOptionsText="No feedback types available"
                  />
                  {errors.dealFeedbackType && (
                    <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.dealFeedbackType.message}</p>
                  )}
                  {availableFeedbackConfigs.length === 0 && (
                    <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>
                      No feedback configurations are available for this product right now.
                    </p>
                  )}
                </div>
              )}

              {/* Order ID */}
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>
                  Order ID *
                  {selectedPlatform && (
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)', marginLeft: '8px' }}>
                      ({platformOrderIdFormats[selectedPlatform.platformName?.toUpperCase()]?.description || 'Enter order ID'})
                    </span>
                  )}
                </label>
                <input
                  {...register('orderId', { 
                    required: 'Order ID is required',
                    validate: (value) => {
                      if (!selectedPlatform) return 'Please select a platform first';
                      const format = platformOrderIdFormats[selectedPlatform.platformName?.toUpperCase()];
                      const cleaned = (value || '').trim().replace(/\s+/g, '');
                      if (format && !format.pattern.test(cleaned)) {
                        return `Invalid format. ${format.description}`;
                      }
                      return true;
                    },
                    onChange: handleOrderIdChange
                  })}
                  type="text"
                  placeholder={selectedPlatform ? platformOrderIdFormats[selectedPlatform.platformName?.toUpperCase()]?.placeholder || 'Enter order ID' : 'Select platform first'}
                  disabled={!selectedPlatform}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    background: 'var(--bg)',
                    color: 'var(--text)',
                    borderColor: errors.orderId ? 'var(--red)' : 'var(--border)',
                    opacity: !selectedPlatform ? 0.6 : 1
                  }}
                />
                {errors.orderId && (
                  <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.orderId.message}</p>
                )}
              </div>

              {/* User Details - Two Column Layout */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
                gap: '16px' 
              }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>
                    User Email * {isAuthenticated && <span style={{ fontSize: '12px', color: 'var(--text-muted)', marginLeft: '4px' }}>(Auto-filled from your account)</span>}
                  </label>
                  <input
                    type="email"
                    name="userEmail"
                    placeholder="email@example.com"
                    readOnly={isAuthenticated}
                    value={userEmail}
                    onChange={(e) => {
                      if (!isAuthenticated) {
                        const val = e.target.value;
                        setUserEmail(val);
                        setValue('userEmail', val, { shouldValidate: true });
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      background: isAuthenticated ? 'var(--bg-deep)' : 'var(--bg)',
                      color: 'var(--text)',
                      borderColor: errors.userEmail ? 'var(--red)' : 'var(--border)',
                      opacity: isAuthenticated ? 0.8 : 1,
                      cursor: isAuthenticated ? 'default' : 'text'
                    }}
                  />
                  {/* Hidden field for React Hook Form to pick up the value */}
                  <input 
                    type="hidden" 
                    {...register('userEmail', { 
                      required: 'Email is required',
                      validate: (value) => {
                        if (!validateEmail(value)) {
                          return 'Please enter a valid email address';
                        }
                        return true;
                      }
                    })} 
                  />
                  {isAuthenticated && (
                    <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                      Email is automatically filled from your logged-in account
                    </p>
                  )}
                  {errors.userEmail && (
                    <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.userEmail.message}</p>
                  )}
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Reviewer Name *</label>
                  <input
                    {...register('reviewerName', { required: 'Reviewer name is required' })}
                    placeholder="John Doe"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      background: 'var(--bg)',
                      color: 'var(--text)',
                      borderColor: errors.reviewerName ? 'var(--red)' : 'var(--border)'
                    }}
                  />
                  {errors.reviewerName && (
                    <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.reviewerName.message}</p>
                  )}
                </div>
              </div>

              {/* WhatsApp Number - Full Width */}
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>WhatsApp Number *</label>
                <div style={{ display: 'flex' }}>
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 12px',
                    background: 'var(--bg-deep)',
                    border: '1px solid var(--border)',
                    borderRight: 'none',
                    borderRadius: '8px 0 0 8px',
                    fontSize: '14px',
                    color: 'var(--text-sub)'
                  }}>+91</span>
                  <input
                    {...register('whatsappNumber', {
                      required: 'WhatsApp number is required',
                      validate: (value) => {
                        if (!validatePhoneNumber(value)) {
                          return 'Please enter exactly 10 digits';
                        }
                        return true;
                      }
                    })}
                    placeholder="9876543210"
                    maxLength={10}
                    onKeyPress={(e) => {
                      // Only allow number keys
                      if (!/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    style={{
                      flex: '1',
                      padding: '12px',
                      border: '1px solid var(--border)',
                      borderRadius: '0 8px 8px 0',
                      background: 'var(--bg)',
                      color: 'var(--text)',
                      borderColor: errors.whatsappNumber ? 'var(--red)' : 'var(--border)'
                    }}
                  />
                </div>
                {errors.whatsappNumber && (
                  <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.whatsappNumber.message}</p>
                )}
              </div>

              {/* Order Details - Two Column Layout */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
                gap: '16px' 
              }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Order Date *</label>
                  <input
                    {...register('orderDate', { required: 'Order date is required' })}
                    type="date"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      background: 'var(--bg)',
                      color: 'var(--text)',
                      borderColor: errors.orderDate ? 'var(--red)' : 'var(--border)'
                    }}
                  />
                  {errors.orderDate && (
                    <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.orderDate.message}</p>
                  )}
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Order Amount *</label>
                  <input type="hidden" {...register('payoutType')} />
                  <input type="hidden" {...register('lessAmount')} />
                  <input type="hidden" {...register('payoutValue')} />
                  <input
                    {...register('orderAmount', { 
                    required: 'Order amount is required',
                    validate: (value) => {
                      const amount = parseFloat(value);
                      if (isNaN(amount) || amount <= 0) {
                        return 'Order amount must be greater than 0';
                      }
                      return true;
                    }
                  })}
                    type="number"
                    step="0.01"
                    placeholder="999.99"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      background: 'var(--bg)',
                      color: 'var(--text)',
                      borderColor: errors.orderAmount ? 'var(--red)' : 'var(--border)'
                    }}
                  />
                  {errors.orderAmount && (
                    <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.orderAmount.message}</p>
                  )}
                  <p style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px' }}>
                    Pre-filled from product price. Change only if you paid a different amount on {selectedPlatform?.platformName || 'the platform'}.
                  </p>
                </div>
              </div>

              {/* Deal Summary - auto from product + feedback type */}
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Deal Summary</label>
                <div style={{ padding: '14px 16px', borderRadius: '10px', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '10px' }}>
                    <div style={{ fontSize: '13px', color: 'var(--text-sub)' }}>
                      Product Amount
                      <div style={{ marginTop: '4px', fontSize: '16px', fontWeight: '700', color: 'var(--text)' }}>
                        ₹{selectedProduct?.productAmount ?? '-'}
                      </div>
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-sub)' }}>
                      Slots
                      <div style={{ marginTop: '4px', fontSize: '16px', fontWeight: '700', color: 'var(--text)' }}>
                        {selectedFeedbackConfig ? `${selectedFeedbackConfig.remainingSlots}/${selectedFeedbackConfig.totalSlots}` : '-'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <RefundPolicyNotice compact />

              {/* Dropdown Fields - Two Column Layout */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
                gap: '16px' 
              }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Mediator *</label>
                  <input type="hidden" {...register('mediatorId', { required: 'Please select a mediator' })} />
                  <SearchableSelect
                    options={mediatorOptions}
                    value={watch('mediatorId')}
                    onChange={(value) => setValue('mediatorId', value, { shouldValidate: true, shouldDirty: true })}
                    placeholder={!selectedProductCode ? 'Select product first' : mediators.length === 0 ? 'No mediators available' : 'Select mediator'}
                    searchPlaceholder="Type mediator name or code..."
                    disabled={!selectedProductCode}
                    error={Boolean(errors.mediatorId)}
                    noOptionsText="No mediators available"
                    disabledText="Select product first"
                  />
                  {errors.mediatorId && (
                    <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.mediatorId.message}</p>
                  )}
                  {selectedProduct?.restrictedToAssignedMediators === false && selectedProductCode && (
                    <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                      All active mediators are available for this product.
                    </p>
                  )}
                  {selectedProduct?.restrictedToAssignedMediators !== false && selectedProductCode && (
                    <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                      Only assigned mediators for this product are available.
                    </p>
                  )}
                </div>
              </div>

                        {/* Demo Screenshot Section */}
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Demo: Order Screenshot Example</label>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => navigate('/guides')}
                  style={{ width: '100%', justifyContent: 'center', marginBottom: '12px' }}
                  icon="info"
                >
                  View Demo Screenshots
                </Button>
                <div style={{
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  padding: isMobile ? '12px' : '16px',
                  background: 'var(--bg-deep)',
                  textAlign: 'center'
                }}>
                  <div style={{ 
                    fontSize: isMobile ? '13px' : '14px', 
                    color: 'var(--text-muted)', 
                    marginBottom: '12px',
                    fontWeight: '500'
                  }}>
                    📸 Example of what your {selectedPlatform?.platformName || 'order'} screenshot should look like:
                  </div>
                  
                  {/* Demo screenshot container */}
                  <div style={{
                    border: '2px dashed var(--border)',
                    borderRadius: '8px',
                    padding: isMobile ? '12px' : '20px',
                    background: 'var(--bg)',
                    minHeight: isMobile ? '160px' : '200px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    {selectedPlatform?.platformName?.toUpperCase() === 'AMAZON' ? (
                      <img 
                        src={AmazonDemoSS} 
                        alt="Amazon Order Demo" 
                        style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '4px' }} 
                      />
                    ) : selectedPlatform?.platformName?.toUpperCase() === 'FLIPKART' ? (
                      <img 
                        src={FlipkartDemoSS} 
                        alt="Flipkart Order Demo" 
                        style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '4px' }} 
                      />
                    ) : (
                      <>
                        <div style={{
                          position: 'absolute',
                          top: '12px',
                          left: '12px',
                          fontSize: '12px',
                          color: 'var(--text-muted)',
                          fontWeight: '500'
                        }}>
                          {selectedPlatform?.platformName || 'PLATFORM'}
                        </div>
                        
                        <div style={{
                          width: '100%',
                          maxWidth: isMobile ? '240px' : '300px',
                          padding: isMobile ? '12px' : '16px',
                          background: 'white',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                          <div style={{ 
                            fontSize: '14px', 
                            fontWeight: 'bold', 
                            marginBottom: '8px',
                            color: '#333'
                          }}>
                            Order Details
                          </div>
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#666', 
                            marginBottom: '4px',
                            textAlign: 'left'
                          }}>
                            Order ID: <span style={{ fontWeight: 'bold' }}>{selectedPlatform ? platformOrderIdFormats[selectedPlatform.platformName?.toUpperCase()]?.placeholder || '123-4567890-1234567' : '123-4567890-1234567'}</span>
                          </div>
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#666', 
                            marginBottom: '4px',
                            textAlign: 'left'
                          }}>
                            Product: Sample Product Name
                          </div>
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#666', 
                            marginBottom: '4px',
                            textAlign: 'left'
                          }}>
                            Amount: ₹999.00
                          </div>
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#666', 
                            textAlign: 'left'
                          }}>
                            Date: DD/MM/YYYY
                          </div>
                        </div>
                      </>
                    )}
                    
                    <div style={{
                      marginTop: '16px',
                      fontSize: '12px',
                      color: 'var(--text-muted)',
                      textAlign: 'center'
                    }}>
                      Make sure your screenshot clearly shows:<br/>
                      • Order ID<br/>
                      • Product Name<br/>
                      • Order Amount<br/>
                      • Order Date
                    </div>
                  </div>
                </div>
              </div>

              {/* Screenshot Upload */}
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Order Screenshot *</label>
                <div style={{
                  border: '2px dashed var(--border)',
                  borderRadius: '8px',
                  padding: isMobile ? '16px' : '24px',
                  textAlign: 'center',
                  background: 'var(--bg-deep)',
                  minHeight: isMobile ? '150px' : '180px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {screenshot ? (
                    <div style={{ width: '100%' }}>
                      <img 
                        src={URL.createObjectURL(screenshot)} 
                        alt="Order screenshot" 
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '120px', 
                          borderRadius: '4px',
                          objectFit: 'contain'
                        }} 
                      />
                      <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: 'var(--text-muted)' }}>
                        {screenshot.name}
                      </p>
                      <button
                        type="button"
                        onClick={() => setScreenshot(null)}
                        style={{
                          marginTop: '8px',
                          padding: '4px 8px',
                          background: 'var(--red)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleScreenshotChange}
                        style={{ display: 'none' }}
                        id="screenshot-upload"
                      />
                      <label htmlFor="screenshot-upload" style={{ cursor: 'pointer' }}>
                        <div style={{ fontSize: '48px', marginBottom: '8px' }}>📷</div>
                        <p style={{ color: 'var(--text)', marginBottom: '4px' }}>
                          Click to upload order screenshot
                        </p>
                        <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                          PNG, JPG up to 10MB
                        </p>
                      </label>
                    </>
                  )}
                </div>
              </div>

              {/* Confirmation Checkbox */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: '12px',
                padding: '16px',
                background: 'var(--bg-deep)',
                borderRadius: '8px',
                border: '1px solid var(--border)'
              }}>
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  style={{ 
                    marginTop: '2px',
                    width: '16px',
                    height: '16px',
                    accentColor: 'var(--primary)'
                  }}
                />
                <label style={{ 
                  fontSize: '14px', 
                  color: 'var(--text)', 
                  cursor: 'pointer',
                  lineHeight: '1.5',
                  flex: 1
                }}>
                  I confirm that all the information provided is accurate and I have uploaded the correct order screenshot.
                </label>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={submitting || !confirmed || !screenshot}
                loading={submitting}
                style={{
                  padding: '14px 24px',
                  fontSize: '16px',
                  fontWeight: '600',
                  borderRadius: '8px'
                }}
              >
                {submitting ? 'Submitting...' : 'Submit Order'}
              </Button>
            </form>
          </div>
        </div>
        </>
        )}
      </div>
    </PublicLayout>
  );
};

export default OrderForm;
